import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { accountAPI } from "../lib/api";
import Header from "../components/Header";
import ImageCropper from "../components/ImageCropper";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  User, Camera, Save, ArrowLeft, Instagram, MapPin, Briefcase, Award, Trash2,
  Link2, Copy, Users, CheckCircle, AlertTriangle, Power
} from "lucide-react";
import api, { referralAPI } from "../lib/api";
import { getImageUrl, getInitials } from "../lib/avatarUtils";

export default function ProfilePage() {
  const { trainer, refreshTrainer, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [imgError, setImgError] = useState(false);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [transferEmail, setTransferEmail] = useState("");
  const [clientCount, setClientCount] = useState(0);
  const [profile, setProfile] = useState({
    name: "",
    bio: "",
    experience: "",
    specialization: "",
    instagram: "",
    location: "",
    profile_photo: ""
  });

  useEffect(() => {
    // Fetch fresh profile data from server (not just from localStorage/auth context)
    const loadProfile = async () => {
      try {
        const res = await api.get('/profile');
        setProfile({
          name: res.data.name || "",
          bio: res.data.bio || "",
          experience: res.data.experience || "",
          specialization: res.data.specialization || "",
          instagram: res.data.instagram || "",
          location: res.data.location || "",
          profile_photo: res.data.profile_photo || ""
        });
      } catch {
        // Fallback to auth context data if API fails
        if (trainer) {
          setProfile({
            name: trainer.name || "",
            bio: trainer.bio || "",
            experience: trainer.experience || "",
            specialization: trainer.specialization || "",
            instagram: trainer.instagram || "",
            location: trainer.location || "",
            profile_photo: trainer.profile_photo || ""
          });
        }
      }
    };
    loadProfile();
    // Load invite link and referrals
    const loadInviteData = async () => {
      try {
        const [linkRes, refRes] = await Promise.all([
          referralAPI.getInviteLink(),
          referralAPI.getMyReferrals()
        ]);
        setInviteLink(linkRes.data.invite_url);
        setInviteCode(linkRes.data.invite_code);
        setReferrals(refRes.data);
      } catch (e) { console.error('Failed to load invite data:', e); }
    };
    if (!trainer?.is_admin) loadInviteData();
  }, [trainer]);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // Read file and open cropper
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCroppedUpload = async (croppedBlob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      const ext = croppedBlob.type === 'image/png' ? 'png' : 'jpg';
      formData.append('file', croppedBlob, `profile.${ext}`);

      const response = await api.post('/profile/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile(prev => ({ ...prev, profile_photo: response.data.url }));
      setImgError(false);
      toast.success("Photo uploaded successfully!");
      if (refreshTrainer) refreshTrainer();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      setCropImage(null);
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      await api.delete('/profile/photo');
      setProfile(prev => ({ ...prev, profile_photo: "" }));
      toast.success("Photo removed");
      if (refreshTrainer) refreshTrainer();
    } catch (error) {
      toast.error("Failed to remove photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!profile.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setLoading(true);
    try {
      // Send only text fields — profile_photo is managed by upload/delete endpoints
      await api.put('/profile', {
        name: profile.name,
        bio: profile.bio,
        experience: profile.experience,
        specialization: profile.specialization,
        instagram: profile.instagram,
        location: profile.location
      });
      toast.success("Profile updated successfully!");
      if (refreshTrainer) refreshTrainer();
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const photoUrl = profile.profile_photo
    ? getImageUrl(profile.profile_photo)
    : null;

  return (
    <div className="min-h-screen pb-8">
      <Header title="Profile" subtitle="Edit your profile" />

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-white/[0.06] bg-card/95">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-wide">Coach Profile</CardTitle>
            <CardDescription>Update your profile information visible to clients</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Photo with Preview + Edit */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative" data-testid="profile-photo-area">
                {/* Main photo - click to preview */}
                <div 
                  className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-primary/20 cursor-pointer"
                  onClick={() => { if (photoUrl && !imgError) setShowPhotoPreview(true); }}
                  data-testid="profile-photo-preview-trigger"
                >
                  {photoUrl && !imgError ? (
                    <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-img" onError={() => setImgError(true)} />
                  ) : (
                    <span className="font-heading text-3xl font-bold text-primary" data-testid="profile-initials">{getInitials(profile.name || trainer?.name)}</span>
                  )}
                </div>
                {/* Edit icon overlay - click to upload */}
                <button
                  onClick={(e) => { e.stopPropagation(); handlePhotoClick(); }}
                  className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary flex items-center justify-center shadow-lg border-2 border-background hover:scale-110 transition-transform"
                  data-testid="profile-photo-edit-btn"
                  title="Change photo"
                >
                  <Camera className="w-4 h-4 text-primary-foreground" />
                </button>
                {uploading && (
                  <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                    <div className="spinner-sm" />
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={handlePhotoChange}
              />
              {photoUrl && !imgError && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemovePhoto}
                  disabled={uploading}
                  className="text-xs rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                  data-testid="remove-photo-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Remove Photo
                </Button>
              )}
              {!photoUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePhotoClick}
                  className="text-xs rounded-lg"
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-3.5 h-3.5 mr-1.5" />
                  Upload Photo
                </Button>
              )}
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your full name"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="profile-name-input"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4" /> Bio / About
              </Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell clients about yourself..."
                className="bg-white/[0.03] border-white/[0.06] min-h-[100px]"
                data-testid="profile-bio-input"
              />
            </div>

            {/* Experience */}
            <div className="space-y-2">
              <Label htmlFor="experience" className="text-sm font-medium flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Experience
              </Label>
              <Input
                id="experience"
                value={profile.experience}
                onChange={(e) => setProfile(prev => ({ ...prev, experience: e.target.value }))}
                placeholder="e.g., 5 years, Certified Personal Trainer"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="profile-experience-input"
              />
            </div>

            {/* Specialization */}
            <div className="space-y-2">
              <Label htmlFor="specialization" className="text-sm font-medium flex items-center gap-2">
                <Award className="w-4 h-4" /> Specialization
              </Label>
              <Input
                id="specialization"
                value={profile.specialization}
                onChange={(e) => setProfile(prev => ({ ...prev, specialization: e.target.value }))}
                placeholder="e.g., Weight Loss, Muscle Building"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="profile-specialization-input"
              />
            </div>

            {/* Instagram */}
            <div className="space-y-2">
              <Label htmlFor="instagram" className="text-sm font-medium flex items-center gap-2">
                <Instagram className="w-4 h-4" /> Instagram (optional)
              </Label>
              <Input
                id="instagram"
                value={profile.instagram}
                onChange={(e) => setProfile(prev => ({ ...prev, instagram: e.target.value }))}
                placeholder="@yourusername"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="profile-instagram-input"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Location (optional)
              </Label>
              <Input
                id="location"
                value={profile.location}
                onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Mumbai, India"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="profile-location-input"
              />
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={loading}
              className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl bg-primary hover:bg-primary/90"
              data-testid="save-profile-btn"
            >
              {loading ? "Saving..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Invite Link Card */}
        {!trainer?.is_admin && inviteLink && (
          <Card className="border-white/[0.06] bg-card/95 mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary" />
                Your Invite Link
              </CardTitle>
              <CardDescription>Share this link with other trainers to invite them to FitTrack Coach</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="bg-white/[0.03] border-white/[0.06] text-sm"
                  data-testid="invite-link-input"
                />
                <Button
                  variant="outline"
                  className="shrink-0 border-primary/50 text-primary hover:bg-primary/10"
                  data-testid="copy-invite-link-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    setCopied(true);
                    toast.success("Invite link copied!");
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              {/* My Referrals */}
              {referrals.length > 0 && (
                <div className="pt-4 border-t border-border/30">
                  <h3 className="text-sm font-bold tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Trainers You Invited ({referrals.length})
                  </h3>
                  <div className="space-y-2">
                    {referrals.map((ref) => (
                      <div
                        key={ref.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/20"
                        data-testid={`referral-${ref.id}`}
                      >
                        <div>
                          <p className="font-medium text-sm text-foreground">{ref.name}</p>
                          <p className="text-xs text-muted-foreground">{ref.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          ref.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                          ref.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                          'bg-red-500/10 text-red-400'
                        }`}>
                          {ref.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Danger Zone */}
        {!trainer?.is_admin && (
          <Card className="glass-card border-0 border-t border-red-500/20 mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase tracking-wide text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                <div>
                  <p className="text-sm font-medium text-foreground">Deactivate Account</p>
                  <p className="text-xs text-muted-foreground">Temporarily disable your account. Contact admin to reactivate.</p>
                </div>
                <Button size="sm" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10" data-testid="deactivate-btn"
                  onClick={async () => {
                    if (!window.confirm("Deactivate your account? You won't be able to log in until admin reactivates it.")) return;
                    try {
                      await accountAPI.deactivate();
                      toast.success("Account deactivated.");
                      logout();
                      navigate("/");
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>
                  <Power className="w-3 h-3 mr-1" /> Deactivate
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-red-500/10">
                <div>
                  <p className="text-sm font-medium text-foreground">Delete Account</p>
                  <p className="text-xs text-muted-foreground">Permanently delete your account. Transfer clients first.</p>
                </div>
                <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-account-btn"
                  onClick={async () => {
                    try {
                      const countRes = await accountAPI.getClientCount();
                      setClientCount(countRes.data.count);
                    } catch { setClientCount(0); }
                    setShowDeleteDialog(true);
                  }}>
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-tight text-red-400">Delete Account</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">This action is permanent and cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {clientCount > 0 && (
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <p className="text-sm text-orange-400 font-medium">You have {clientCount} client(s). Transfer them first.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Transfer to coach email</Label>
                  <Input value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)} placeholder="other.coach@email.com" className="h-11 bg-secondary/50 border-transparent" data-testid="transfer-email-input" />
                </div>
                <Button onClick={async () => {
                  if (!transferEmail) { toast.error("Enter target coach email"); return; }
                  try {
                    const res = await accountAPI.transferClients(transferEmail);
                    toast.success(res.data.message);
                    setClientCount(0);
                    setTransferEmail("");
                  } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
                }} className="w-full h-10 btn-premium text-white font-semibold text-xs uppercase rounded-xl" data-testid="transfer-clients-btn">
                  Transfer Clients
                </Button>
              </div>
            )}
            {clientCount === 0 && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Type DELETE to confirm</Label>
                  <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" className="h-11 bg-secondary/50 border-transparent font-mono" data-testid="delete-confirm-input" />
                </div>
                <Button onClick={async () => {
                  if (deleteConfirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
                  try {
                    await accountAPI.deleteAccount("DELETE");
                    toast.success("Account deleted.");
                    logout();
                    navigate("/");
                  } catch (e) { toast.error(e.response?.data?.detail || "Deletion failed"); }
                }} disabled={deleteConfirm !== "DELETE"} className="w-full h-10 bg-red-500/20 text-red-400 hover:bg-red-500/30 font-semibold text-xs uppercase rounded-xl border border-red-500/30" data-testid="confirm-delete-btn">
                  Permanently Delete Account
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => { setShowCropper(false); setCropImage(null); }}
        imageSrc={cropImage}
        onCropComplete={handleCroppedUpload}
        aspectRatio={1}
      />

      {/* Full-screen Photo Preview Modal */}
      {showPhotoPreview && photoUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
          onClick={() => setShowPhotoPreview(false)}
          data-testid="photo-preview-overlay"
        >
          <button
            onClick={() => setShowPhotoPreview(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="close-preview-btn"
            style={{ top: "calc(1rem + env(safe-area-inset-top, 0px))" }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <img
            src={photoUrl}
            alt="Profile Preview"
            className="max-w-[90vw] max-h-[80vh] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="photo-preview-img"
          />
          <div className="absolute bottom-8 flex gap-3" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowPhotoPreview(false); handlePhotoClick(); }}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 shadow-lg"
              data-testid="preview-change-photo-btn"
            >
              <Camera className="w-4 h-4" />
              Change Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
